import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index";

// Lazy load route components
const Services = lazy(() => import("./pages/Services"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Devis = lazy(() => import("./pages/Devis"));
const CGV = lazy(() => import("./pages/CGV"));
const Vitrerie = lazy(() => import("./pages/services/Vitrerie"));
const Facades = lazy(() => import("./pages/services/Facades"));
const Bureaux = lazy(() => import("./pages/services/Bureaux"));
const EspacesVerts = lazy(() => import("./pages/services/EspacesVerts"));
const Boutiques = lazy(() => import("./pages/services/Boutiques"));
const Industriel = lazy(() => import("./pages/services/Industriel"));
const Commerces = lazy(() => import("./pages/secteurs/Commerces"));
const Industries = lazy(() => import("./pages/secteurs/Industries"));
const Collectivites = lazy(() => import("./pages/secteurs/Collectivites"));
const Nantes = lazy(() => import("./pages/zones/Nantes"));
const SaintHerblain = lazy(() => import("./pages/zones/SaintHerblain"));
const Orvault = lazy(() => import("./pages/zones/Orvault"));
const Vertou = lazy(() => import("./pages/zones/Vertou"));
const Reze = lazy(() => import("./pages/zones/Reze"));
const Carquefou = lazy(() => import("./pages/zones/Carquefou"));
const Bouguenais = lazy(() => import("./pages/zones/Bouguenais"));
const Blog = lazy(() => import("./pages/Blog"));
const PourquoiFaireAppelProfessionnel = lazy(() => import("./pages/blog/PourquoiFaireAppelProfessionnel"));
const NettoyageEcologiqueAvantages = lazy(() => import("./pages/blog/NettoyageEcologiqueAvantages"));
const FrequenceNettoyageBureaux = lazy(() => import("./pages/blog/FrequenceNettoyageBureaux"));
const TarifNettoyageProfessionnel = lazy(() => import("./pages/blog/TarifNettoyageProfessionnel"));
const NettoyageApresTravaux = lazy(() => import("./pages/blog/NettoyageApresTravaux"));
const NettoyageVitresHauteur = lazy(() => import("./pages/blog/NettoyageVitresHauteur"));
const DesinfectionHygieneEntreprise = lazy(() => import("./pages/blog/DesinfectionHygieneEntreprise"));
const NettoyageSols = lazy(() => import("./pages/blog/NettoyageSols"));
const NettoyageFacades = lazy(() => import("./pages/blog/NettoyageFacades"));
const QualiteAirInterieur = lazy(() => import("./pages/blog/QualiteAirInterieur"));
const EntretienEspacesVerts = lazy(() => import("./pages/blog/EntretienEspacesVerts"));
const ChecklistNettoyageBureaux = lazy(() => import("./pages/blog/ChecklistNettoyageBureaux"));
const ProduitsNettoyageProfessionnels = lazy(() => import("./pages/blog/ProduitsNettoyageProfessionnels"));
const NettoyageCommerces = lazy(() => import("./pages/blog/NettoyageCommerces"));
const NettoyageIndustriel = lazy(() => import("./pages/blog/NettoyageIndustriel"));
const GestionDechetsEntreprise = lazy(() => import("./pages/blog/GestionDechetsEntreprise"));
const MaterielNettoyageProfessionnel = lazy(() => import("./pages/blog/MaterielNettoyageProfessionnel"));
const NettoyageSanitaires = lazy(() => import("./pages/blog/NettoyageSanitaires"));
const OptimiserContratsNettoyage = lazy(() => import("./pages/blog/OptimiserContratsNettoyage"));
const NettoyageHivernal = lazy(() => import("./pages/blog/NettoyageHivernal"));
const NettoyageEspacesVertsEntreprise = lazy(() => import("./pages/blog/NettoyageEspacesVertsEntreprise"));
const NettoyageBureauCarquefou = lazy(() => import("./pages/blog/NettoyageBureauCarquefou"));
const EntretienEspacesVertsNantes = lazy(() => import("./pages/blog/EntretienEspacesVertsNantes"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/a-propos" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/devis" element={<Devis />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/cgv" element={<CGV />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/vitrerie" element={<Vitrerie />} />
            <Route path="/services/bureaux" element={<Bureaux />} />
            <Route path="/services/facades" element={<Facades />} />
            <Route path="/services/espaces-verts" element={<EspacesVerts />} />
            <Route path="/services/boutiques" element={<Boutiques />} />
            <Route path="/services/industriel" element={<Industriel />} />
            <Route path="/secteurs/collectivites" element={<Collectivites />} />
            <Route path="/secteurs/commerces" element={<Commerces />} />
            <Route path="/secteurs/industries" element={<Industries />} />
            <Route path="/zones/nantes" element={<Nantes />} />
            <Route path="/zones/saint-herblain" element={<SaintHerblain />} />
            <Route path="/zones/reze" element={<Reze />} />
            <Route path="/zones/orvault" element={<Orvault />} />
            <Route path="/zones/vertou" element={<Vertou />} />
            <Route path="/zones/carquefou" element={<Carquefou />} />
            <Route path="/zones/bouguenais" element={<Bouguenais />} />
            <Route path="/blog/pourquoi-faire-appel-professionnel-nettoyage" element={<PourquoiFaireAppelProfessionnel />} />
            <Route path="/blog/frequence-nettoyage-bureaux" element={<FrequenceNettoyageBureaux />} />
            <Route path="/blog/nettoyage-ecologique-avantages" element={<NettoyageEcologiqueAvantages />} />
            <Route path="/blog/tarif-nettoyage-professionnel" element={<TarifNettoyageProfessionnel />} />
            <Route path="/blog/nettoyage-apres-travaux" element={<NettoyageApresTravaux />} />
            <Route path="/blog/nettoyage-vitres-hauteur" element={<NettoyageVitresHauteur />} />
            <Route path="/blog/desinfection-hygiene-entreprise" element={<DesinfectionHygieneEntreprise />} />
            <Route path="/blog/nettoyage-sols-professionnels" element={<NettoyageSols />} />
            <Route path="/blog/nettoyage-facades" element={<NettoyageFacades />} />
            <Route path="/blog/qualite-air-interieur" element={<QualiteAirInterieur />} />
            <Route path="/blog/entretien-espaces-verts" element={<EntretienEspacesVerts />} />
            <Route path="/blog/checklist-nettoyage-bureaux" element={<ChecklistNettoyageBureaux />} />
            <Route path="/blog/produits-nettoyage-professionnels" element={<ProduitsNettoyageProfessionnels />} />
            <Route path="/blog/nettoyage-commerces" element={<NettoyageCommerces />} />
            <Route path="/blog/nettoyage-industriel" element={<NettoyageIndustriel />} />
            <Route path="/blog/gestion-dechets-entreprise" element={<GestionDechetsEntreprise />} />
            <Route path="/blog/materiel-nettoyage-professionnel" element={<MaterielNettoyageProfessionnel />} />
            <Route path="/blog/nettoyage-sanitaires" element={<NettoyageSanitaires />} />
            <Route path="/blog/optimiser-contrats-nettoyage" element={<OptimiserContratsNettoyage />} />
            <Route path="/blog/nettoyage-hivernal" element={<NettoyageHivernal />} />
            <Route path="/blog/nettoyage-espaces-verts-entreprise" element={<NettoyageEspacesVertsEntreprise />} />
            <Route path="/blog/nettoyage-bureau-carquefou" element={<NettoyageBureauCarquefou />} />
            <Route path="/blog/entretien-espaces-verts-nantes" element={<EntretienEspacesVertsNantes />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
